/**
 * Token Refresh Orchestrator Service
 * 
 * Centralizes all token refresh logic from multiple sources.
 * Prevents duplicate refresh attempts and coordinates refresh across tabs.
 * 
 * Issue: #202
 */

import { Injectable } from '@angular/core'
import { Observable, Subject, throwError, of } from 'rxjs'
import {
  debounceTime,
  concatMap,
  switchMap,
  shareReplay,
  finalize,
  catchError,
  tap
} from 'rxjs/operators'

import { AuthenticationService } from '@/services/authentication.service'
import { TokenRefreshService } from '@/services/token-refresh.service'
import { SessionStateService, SessionState } from '@/services/session-state.service'
import { ErrorClassifierService } from '@/services/error-classifier.service'
import { log, error } from '@/util/logger'

export enum RefreshSource {
  Interceptor = 'interceptor', // 401 from HTTP request
  Periodic = 'periodic', // Scheduled refresh
  WebSocket = 'websocket', // WebSocket auth failure
  Proactive = 'proactive', // Token about to expire
  Manual = 'manual', // User-initiated
  HealthCheck = 'health_check' // Health monitor triggered
}

export interface RefreshRequest {
  source: RefreshSource
  reason?: string
  timestamp: number
  priority?: number // Higher = more urgent (default: 0)
}

/**
 * Orchestrates token refresh requests from multiple sources.
 * Ensures only one refresh happens at a time and deduplicates rapid requests.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshOrchestrator {
  private _refreshQueue = new Subject<RefreshRequest>()
  private _activeRefresh$: Observable<any> | null = null
  private _lastRefreshTime: number = 0
  private readonly _minRefreshInterval = 1000 // Minimum 1 second between refreshes

  constructor(
    private _tokenRefresh: TokenRefreshService,
    private _auth: AuthenticationService,
    private _sessionState: SessionStateService,
    private _errorClassifier: ErrorClassifierService
  ) {
    // Process refresh queue with concurrency control
    this._refreshQueue
      .pipe(
        // Deduplicate rapid requests from same source
        debounceTime(500),
        // Process one refresh at a time
        concatMap(request => this._executeRefresh(request))
      )
      .subscribe({
        next: () => {
          log('[TokenRefreshOrchestrator] Refresh completed successfully')
        },
        error: err => {
          error('[TokenRefreshOrchestrator] Refresh failed', err)
        }
      })
  }

  /**
   * Request a token refresh from any source
   * Returns an observable that completes when refresh is done
   */
  requestRefresh(
    source: RefreshSource,
    reason?: string,
    priority: number = 0
  ): Observable<any> {
    // Check if we're already refreshing
    if (this._activeRefresh$) {
      log(
        `[TokenRefreshOrchestrator] Refresh already in progress, queuing request from ${source}`
      )
      return this._activeRefresh$
    }

    // Check minimum interval
    const now = Date.now()
    const timeSinceLastRefresh = now - this._lastRefreshTime
    if (timeSinceLastRefresh < this._minRefreshInterval) {
      log(
        `[TokenRefreshOrchestrator] Refresh requested too soon (${timeSinceLastRefresh}ms ago), queuing`
      )
      // Return existing refresh if it's very recent
      if (this._activeRefresh$) {
        return this._activeRefresh$
      }
    }

    const request: RefreshRequest = {
      source,
      reason,
      timestamp: now,
      priority
    }

    log(`[TokenRefreshOrchestrator] Refresh requested from ${source}`, {
      reason,
      priority
    })

    // Create refresh observable
    this._activeRefresh$ = this._executeRefresh(request).pipe(
      shareReplay(1),
      finalize(() => {
        this._activeRefresh$ = null
        this._lastRefreshTime = Date.now()
      })
    )

    return this._activeRefresh$
  }

  /**
   * Execute a refresh request
   */
  private _executeRefresh(request: RefreshRequest): Observable<any> {
    // Update session state
    this._sessionState.transitionTo(SessionState.Refreshing, request)

    const currentUser = this._auth.currentUserValue
    if (!currentUser) {
      const error = new Error('No user logged in')
      this._sessionState.transitionTo(SessionState.Error, { error, request })
      return throwError(error)
    }

    const refreshToken = currentUser.refresh_token
    if (!refreshToken) {
      const error = new Error('No refresh token available')
      this._sessionState.transitionTo(SessionState.Error, { error, request })
      return throwError(error)
    }

    log(
      `[TokenRefreshOrchestrator] Executing refresh from ${request.source}`,
      request.reason || ''
    )

    return this._tokenRefresh.refreshToken(refreshToken).pipe(
      switchMap(response => {
        // Apply refresh response to authentication service
        // Note: We access the private method via bracket notation
        // This is acceptable as the orchestrator is part of the auth system
        const user = (this._auth as any)._applyRefreshResponse(response)
        if (!user) {
          const error = new Error('Refresh response did not return user')
          this._sessionState.transitionTo(SessionState.Error, { error, request })
          return throwError(error)
        }

        // Success - transition to authenticated
        this._sessionState.transitionTo(SessionState.Authenticated, {
          source: request.source,
          timestamp: Date.now()
        })

        return of(user)
      }),
      catchError(err => {
        // Classify the error
        const classification = this._errorClassifier.classify(err)

        error(
          `[TokenRefreshOrchestrator] Refresh failed from ${request.source}`,
          err,
          classification
        )

        // Determine next state based on error classification
        if (classification.type === 'refresh_token_expired') {
          this._sessionState.transitionTo(SessionState.Expired, {
            error: err,
            request,
            classification
          })
        } else {
          this._sessionState.transitionTo(SessionState.Error, {
            error: err,
            request,
            classification
          })
        }

        return throwError(err)
      }),
      tap({
        next: () => {
          log(
            `[TokenRefreshOrchestrator] Refresh successful from ${request.source}`
          )
        }
      })
    )
  }

  /**
   * Check if a refresh is currently in progress
   */
  get isRefreshing(): boolean {
    return this._activeRefresh$ !== null
  }

  /**
   * Get the active refresh observable (if any)
   */
  get activeRefresh$(): Observable<any> | null {
    return this._activeRefresh$
  }
}
